'use client';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import { useEffect, useRef, useState } from 'react';
import {
  UNIVERSES as SOURCE_UNIVERSES,
  getUniverse,
  selectedCapabilities,
  type Universe,
} from '@/lib/public/universes';
import {
  DRAFT_KEY,
  emptyDraft,
  recoverDraft,
  type Contact,
  type ProjectDraft,
} from '@/lib/public/project-draft';
import { Arrow } from './Icons';
import RNodePerimeters from './RNodePerimeters';
import usePanelTransition from '../usePanelTransition';
import usePublicProtection from '../elina/usePublicProtection';

export default function ProjectConfigurator({
  universe: u,
  intakeEnabled,
}: {
  universe: Universe;
  intakeEnabled: boolean;
}) {
  const { t, href, content, locale } = useI18n();
  const UNIVERSES = content(SOURCE_UNIVERSES);

  const draftKey = DRAFT_KEY + (locale === 'fr' ? '' : ':' + locale);
  const protection = usePublicProtection();
  const [draft, setDraft] = useState<ProjectDraft>(() => emptyDraft(u.id));
  const [ready, setReady] = useState(false);
  const [stage, setStage] = useState(0);
  const [group, setGroup] = useState(u.capabilities[0].group);
  const groupPanelRef = usePanelTransition(group);
  const [contact, setContact] = useState<Contact>({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    consent: false,
    website: '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  const [saved, setSaved] = useState(true);
  const [exported, setExported] = useState(false);
  const requestId = useRef('');
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    setGroup(u.capabilities[0].group);
    setStage(0);
    setReference('');
    try {
      const recovered = recoverDraft(sessionStorage.getItem(draftKey), u.id);
      const contexts: Record<string, string> = {
        entreprises: t('Entreprise'),
        associations: t('Association'),
        formation: t('Organisme de formation'),
        institutions: t('Institution ou collectivité'),
      };
      const context = new URLSearchParams(window.location.search).get('secteur');
      if (u.id === 'rise' && !recovered.activity.trim() && context && Object.prototype.hasOwnProperty.call(contexts, context)) {
        recovered.activity = contexts[context];
      }
      setDraft(recovered);
    } catch {
      setSaved(false);
    }
    setReady(true);
  }, [u.id, u.capabilities]);
  useEffect(() => {
    if (!ready) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(draft));
      setSaved(true);
    } catch {
      setSaved(false);
    }
    requestId.current = '';
    setError('');
    setExported(false);
  }, [draft, ready]);
  const selected = content(selectedCapabilities(draft.selected));
  const groups = [...new Set(u.capabilities.map((c) => c.group))];
  const recommendCopilot = u.id === 'r-node';
  const update = (
    key: keyof Omit<ProjectDraft, 'version' | 'selected' | 'entryUniverse'>,
    value: string
  ) => setDraft((d) => ({ ...d, [key]: value }));
  const toggle = (id: string) =>
    setDraft((d) => ({
      ...d,
      selected: d.selected.includes(id) ? d.selected.filter((x) => x !== id) : [...d.selected, id],
    }));
  const go = (next: number) => {
    if (sending) return;
    setStage(next);
    setError('');
    window.setTimeout(() => {
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }, 0);
  };
  const setField = <K extends keyof Contact>(key: K, value: Contact[K]) => {
    if (sending) return;
    setContact((c) => ({ ...c, [key]: value }));
    requestId.current = '';
    setError('');
  };
  const exportSummary = () => {
    const lines = [
      t('RDV-PARTNERS — SYNTHÈSE DE VOTRE PROJET'),
      reference
        ? t('Demande enregistrée : {reference} — aucun paiement demandé.', { reference })
        : t('Document de préparation — aucun envoi effectué, aucun paiement demandé.'),
      '',
      t('Univers de départ :') + ' ' + getUniverse(draft.entryUniverse)?.name,
      ...UNIVERSES.flatMap((v) => {
        const caps = selected.filter((c) => c.universe === v.id);
        return caps.length ? ['', v.name, ...caps.map((c) => '• ' + c.title)] : [];
      }),
      '',
      t('Activité :') + ' ' + (draft.activity || t('À préciser')),
      t('Équipe :') + ' ' + (draft.team || t('À préciser')),
      t('Sites :') + ' ' + (draft.sites || t('À préciser')),
      t('Outils :') + ' ' + (draft.tools || t('À préciser')),
      t('Calendrier :') + ' ' + (draft.timing || t('À définir')),
      t('Objectif :') + ' ' + (draft.objective || t('À préciser')),
      '',
      t('Contact :') + ' ' + contact.firstName + ' ' + contact.lastName,
      t('Entreprise :') + ' ' + (contact.company),
      t('Email :') + ' ' + (contact.email),
    ];
    const url = URL.createObjectURL(
      new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Mon-projet-RDV-Partners.txt';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setExported(true);
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    if (!intakeEnabled) {
      exportSummary();
      return;
    }
    setSending(true);
    setError('');
    if (!requestId.current) requestId.current = crypto.randomUUID();
    const controller = new AbortController();
    try {
      const response = await protection.fetchProtected('/api/public/project-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId: requestId.current, draft, contact }),
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || !data.reference)
        throw new Error(
          data.error || t('Votre demande n’a pas pu être transmise. Vos choix sont conservés.')
        );
      setReference(data.reference);
    } catch (e) {
      setError(
        e instanceof Error && e.name === 'AbortError'
          ? t('La connexion a été interrompue. Vous pouvez réessayer sans créer de doublon.')
          : e instanceof Error
            ? e.message
            : t('Envoi impossible. Réessayez.')
      );
    } finally {
      setSending(false);
    }
  };
  return (
    <section id="configurateur" className="u-config-section" aria-labelledby="config-title">
      <div className="u-wrap">{protection.challenge}
        <div className="u-section-heading">
          <p className="u-eyebrow">{t("02 — Composer votre projet")}</p>
          <h2 id="config-title" ref={heading} tabIndex={-1}>
            {t("Que souhaitez-vous")}<br />
            <span>{t("rendre possible ?")}</span>
          </h2>
          <p>
            {t("Choisissez ce qui compte pour vous. Nous préciserons ensemble les solutions et leur mise en œuvre.")}</p>
        </div>
        <ol className="u-steps" aria-label={t("Étapes de configuration")}>
          {[t('Vos besoins'), t('Votre contexte'), t('Votre synthèse')].map((s, i) => (
            <li key={s} className={stage === i ? 'is-current' : stage > i ? 'is-done' : ''}>
              <button
                type="button"
                disabled={sending || !!reference || (i > 0 && !selected.length)}
                onClick={() => go(i)}
                aria-current={stage === i ? 'step' : undefined}
              >
                <span>{stage > i ? '✓' : `0${i + 1}`}</span>
                {s}
              </button>
            </li>
          ))}
        </ol>
        <div className="u-config-layout">
          <div className="u-config-main">
            {reference ? (
              <div className="u-success" role="status">
                <span className="u-check">✓</span>
                <p className="u-eyebrow">{t("Demande enregistrée")}</p>
                <h3>{t("Votre projet prend forme.")}</h3>
                <p>{t("Votre demande de devis est bien arrivée dans notre espace commercial. Nous allons étudier vos besoins et vous recontacter pour préciser le périmètre.")}</p><p>{t("Aucun paiement ni création de compte ne sont nécessaires à cette étape.")}</p>
                <p>
                  {t("Votre référence : ")}<strong>{reference}</strong>
                </p>
                <button
                  type="button"
                  className="u-button u-button-secondary"
                  onClick={exportSummary}
                >
                  {t("Conserver mon récapitulatif ")}<Arrow />
                </button>
              </div>
            ) : (
              <>
                {stage === 0 && (
                  <>
                    {recommendCopilot ? (
                      <RNodePerimeters
                        capabilities={u.capabilities}
                        selectedIds={draft.selected}
                        activeGroup={group}
                        onGroupChange={setGroup}
                        onToggle={toggle}
                        onContinue={() => go(1)}
                        continueDisabled={!ready || !selected.length}
                      />
                    ) : (
                      <>
                        <div className="u-config-heading">
                          <h3>{t("Votre périmètre ")}{u.name}</h3>
                          <span>{t("Plusieurs choix possibles")}</span>
                        </div>
                        <div
                          className="u-filter-tabs"
                          role="group"
                          aria-label={t("Catégories de besoins")}
                        >
                          {groups.map((g) => (
                            <button
                              type="button"
                              key={g}
                              onClick={() => setGroup(g)}
                              onPointerEnter={(event) => {
                                if (event.pointerType === 'mouse') setGroup(g);
                              }}
                              onFocus={() => setGroup(g)}
                              aria-pressed={group === g}
                            >
                              {g}
                            </button>
                          ))}
                        </div>
                        <div className="u-capabilities" ref={groupPanelRef}>
                          {u.capabilities
                            .filter((c) => c.group === group)
                            .map((c) => (
                              <label
                                key={c.id}
                                className={`u-capability ${draft.selected.includes(c.id) ? 'is-selected' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={draft.selected.includes(c.id)}
                                  onChange={() => toggle(c.id)}
                                />
                                <span>
                                  <strong>{c.title}</strong>
                                  <small>{c.detail}</small>
                                </span>
                              </label>
                            ))}
                        </div>
                      </>
                    )}
                    <div className="u-cross-options">
                      <h4>{t("Compléter votre projet")}</h4>
                      <p>{t("Ajoutez un premier besoin dans un autre univers.")}</p>
                      {UNIVERSES.filter(
                        (v) => v.id !== u.id && !(recommendCopilot && v.id === 'elina')
                      ).map((v) => (
                        <label key={v.id}>
                          <input
                            type="checkbox"
                            checked={draft.selected.includes(v.capabilities[0].id)}
                            onChange={() => toggle(v.capabilities[0].id)}
                          />
                          <span>
                            <strong>{v.name}</strong>
                            {v.capabilities[0].title}
                          </span>
                        </label>
                      ))}
                    </div>
                    {!recommendCopilot && (
                      <div className="u-config-actions">
                        <button
                          className="u-button"
                          disabled={!ready || !selected.length}
                          type="button"
                          onClick={() => go(1)}
                        >
                          {t("Préciser mon contexte ")}<Arrow />
                        </button>
                        <span>
                          {selected.length
                            ? t('Besoins sélectionnés : {count}', { count: selected.length })
                            : t('Sélectionnez au moins un besoin.')}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {stage === 1 && (
                  <>
                    <div className="u-config-heading">
                      <h3>{t("Parlons de votre organisation.")}</h3>
                      <span>{t("Ces informations peuvent être précisées plus tard.")}</span>
                    </div>
                    <div className="u-form-grid">
                      <label>
                        {t("Votre activité")}<input
                          value={draft.activity}
                          maxLength={300}
                          placeholder={t("Ex. négoce, services, industrie…")}
                          onChange={(e) => update('activity', e.target.value)}
                        />
                      </label>
                      <label>
                        {t("Votre équipe")}<select value={draft.team} onChange={(e) => update('team', e.target.value)}>
                          <option value="">{t("À préciser")}</option>
                          {[
                            t('1 personne'),
                            t('2 à 10 personnes'),
                            t('11 à 50 personnes'),
                            t('51 à 250 personnes'),
                            t('Plus de 250 personnes'),
                          ].map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        {t("Vos sites")}<select
                          value={draft.sites}
                          onChange={(e) => update('sites', e.target.value)}
                        >
                          <option value="">{t("À préciser")}</option>
                          {[t('Un site'), t('2 à 5 sites'), t('Plus de 5 sites')].map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        {t("Votre calendrier")}<select
                          value={draft.timing}
                          onChange={(e) => update('timing', e.target.value)}
                        >
                          <option value="">{t("À définir ensemble")}</option>
                          {[
                            t('Dès que possible'),
                            t('Dans les 3 mois'),
                            t('Dans les 6 mois'),
                            t('Je prépare un projet à venir'),
                          ].map((v) => (
                            <option key={v}>{v}</option>
                          ))}
                        </select>
                      </label>
                      <label className="u-field-full">
                        {t("Vos outils actuels")}<input
                          value={draft.tools}
                          maxLength={300}
                          placeholder={t("Logiciels, fichiers, site ou applications à conserver…")}
                          onChange={(e) => update('tools', e.target.value)}
                        />
                      </label>
                      <label className="u-field-full">
                        {t("Ce que vous aimeriez améliorer en priorité")}<textarea
                          value={draft.objective}
                          maxLength={1500}
                          rows={4}
                          placeholder={t("Racontez-nous votre situation en quelques mots.")}
                          onChange={(e) => update('objective', e.target.value)}
                        />
                      </label>
                    </div>
                    <div className="u-config-actions">
                      <button type="button" className="u-text-button" onClick={() => go(0)}>
                        {t("← Mes besoins")}</button>
                      <button type="button" className="u-button" onClick={() => go(2)}>
                        {t("Voir ma synthèse ")}<Arrow />
                      </button>
                    </div>
                  </>
                )}
                {stage === 2 && (
                  <>
                    <div className="u-config-heading">
                      <h3>{t("Votre projet, dans son ensemble.")}</h3>
                      <button type="button" className="u-text-button" onClick={() => go(0)}>
                        {t("Modifier les besoins")}</button>
                    </div>
                    <div className="u-checkout-list">
                      {UNIVERSES.map((v) => {
                        const caps = selected.filter((c) => c.universe === v.id);
                        return caps.length ? (
                          <div key={v.id}>
                            <h4>
                              {v.name}
                              <span>
                                {t('Besoins : {count}', { count: caps.length })}
                              </span>
                            </h4>
                            <ul>
                              {caps.map((c) => (
                                <li key={c.id}>
                                  <span aria-hidden="true">✓</span>
                                  {c.title}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null;
                      })}
                    </div>
                    <div className="u-context-summary">
                      <div>
                        <p className="u-eyebrow">{t("Votre contexte")}</p>
                        <p>
                          {[draft.activity, draft.team, draft.sites, draft.timing]
                            .filter(Boolean)
                            .join(' · ') || t('À préciser lors de notre échange.')}
                        </p>
                        {draft.tools && <p>{t("Outils : ")}{draft.tools}</p>}
                        {draft.objective && <p>{draft.objective}</p>}
                      </div>
                      <button className="u-text-button" type="button" onClick={() => go(1)}>
                        {t("Modifier")}</button>
                    </div>
                    <div className="u-quote-note">
                      <strong>{t("Une proposition à votre mesure.")}</strong>
                      <p>
                        {t("Votre commercial confirme le périmètre, les connexions et le calendrier avant de vous remettre un devis. Aucun paiement à cette étape.")}</p>
                    </div>
                    <form onSubmit={submit} aria-busy={sending}>
                      <fieldset disabled={sending} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                      <h3 className="u-contact-title">{t("Comment vous recontacter ?")}</h3>
                      <div className="u-form-grid">
                        <label>
                          {t("Prénom")}{intakeEnabled ? ' *' : ''}
                          <input
                            autoComplete="given-name"
                            required={intakeEnabled}
                            maxLength={80}
                            value={contact.firstName}
                            onChange={(e) => setField('firstName', e.target.value)}
                          />
                        </label>
                        <label>
                          {t("Nom")}{intakeEnabled ? ' *' : ''}
                          <input
                            autoComplete="family-name"
                            required={intakeEnabled}
                            maxLength={80}
                            value={contact.lastName}
                            onChange={(e) => setField('lastName', e.target.value)}
                          />
                        </label>
                        <label>
                          {t("Entreprise")}{intakeEnabled ? ' *' : ''}
                          <input
                            autoComplete="organization"
                            required={intakeEnabled}
                            maxLength={160}
                            value={contact.company}
                            onChange={(e) => setField('company', e.target.value)}
                          />
                        </label>
                        <label>
                          {t("Email professionnel")}{intakeEnabled ? ' *' : ''}
                          <input
                            type="email"
                            autoComplete="email"
                            required={intakeEnabled}
                            maxLength={254}
                            value={contact.email}
                            onChange={(e) => setField('email', e.target.value)}
                          />
                        </label>
                        <label>
                          {t("Téléphone ")}<span className="u-optional">{t("(facultatif)")}</span>
                          <input
                            type="tel"
                            autoComplete="tel"
                            maxLength={40}
                            value={contact.phone}
                            onChange={(e) => setField('phone', e.target.value)}
                          />
                        </label>
                        <label className="u-honeypot" aria-hidden="true">
                          {t("Votre site")}<input
                            tabIndex={-1}
                            autoComplete="off"
                            value={contact.website}
                            onChange={(e) => setField('website', e.target.value)}
                          />
                        </label>
                      </div>
                      <label className="u-consent">
                        <input
                          type="checkbox"
                          checked={contact.consent}
                          required={intakeEnabled}
                          onChange={(e) => setField('consent', e.target.checked)}
                        />
                        <span>
                          {t("J’accepte d’être recontacté par RDV-Partners au sujet de ce projet. Ces informations servent à étudier ma demande.")}</span>
                      </label>
                      {!intakeEnabled && (
                        <p className="u-preview-note">
                          {t("Version de prévisualisation : vous pouvez conserver votre synthèse. Aucune demande n’est encore transmise au commercial.")}</p>
                      )}
                      {error && (
                        <p className="u-form-error" role="alert">
                          {error}
                        </p>
                      )}
                      {exported && (
                        <p role="status" className="u-export-success">
                          {t("Votre récapitulatif a été téléchargé. Aucun envoi n’a été effectué.")}</p>
                      )}
                      <div className="u-config-actions">
                        <button
                          className="u-button"
                          type="submit"
                          disabled={sending || !selected.length}
                        >
                          {sending
                            ? t('Transmission en cours…')
                            : intakeEnabled
                              ? t('Transmettre mon projet pour devis')
                              : t('Conserver mon récapitulatif')}
                          <Arrow />
                        </button>
                        {intakeEnabled && (
                          <button className="u-text-button" type="button" onClick={exportSummary}>
                            {t("Télécharger une copie")}</button>
                        )}
                      </div>
                      </fieldset>
                    </form>
                  </>
                )}
              </>
            )}
          </div>
          <aside className="u-project-summary" aria-label={t("Résumé de votre sélection")}>
            <p className="u-eyebrow">{t("Votre projet")}</p>
            <h3>{t("À votre mesure.")}</h3>
            <p className="u-summary-intro">{t("Un même projet, des univers qui se complètent.")}</p>
            <div className="u-summary-count">
              <strong>{String(selected.length).padStart(2, '0')}</strong>
              <span>
                {t('Besoins sélectionnés')}
              </span>
            </div>
            {selected.length ? (
              <ul>
                {selected.map((c) => (
                  <li key={c.id}>
                    <span>
                      <small>{getUniverse(c.universe)?.name}</small>
                      {c.title}
                    </span>
                    <button
                      type="button"
                      aria-label={t("Retirer : {title}", { title: c.title })}
                      disabled={sending || !!reference}
                      onClick={() => {
                        toggle(c.id);
                        if (selected.length === 1) setStage(0);
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="u-summary-empty">
                {t("Vos choix apparaîtront ici au fil de la configuration.")}</p>
            )}
            <p className="u-summary-save">
              {saved
                ? t('✓ Vos choix sont conservés pendant cette visite.')
                : t('Vos choix restent disponibles sur cette page.')}
            </p>
            <div className="u-summary-next">
              <span>{t("La suite, ensemble.")}</span>
              <p>{t("Un expert RDV-Partners précise votre projet et prépare votre devis.")}</p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

